import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const ProfilePage = () => {
  const { user, updateProfile, deleteProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    username: user?.username || '',
    mail: user?.mail || '',
    password: '',
    confirmPassword: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
    setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setMessage('');

    // Validate passwords if provided
    if (formData.password && formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    // Prepare update data (only include fields that have values)
    const updateData = {};
    
    if (formData.username && formData.username !== user?.username) {
      updateData.username = formData.username;
    }
    
    if (formData.mail && formData.mail !== user?.mail) {
      updateData.mail = formData.mail;
    }
    
    if (formData.password) {
      updateData.password = formData.password;
    }

    // Check if there's anything to update
    if (Object.keys(updateData).length === 0) {
      setError('No changes detected');
      setIsLoading(false);
      return;
    }

    const result = await updateProfile(updateData);
    
    if (result.success) {
      setMessage('Profile updated successfully!');
      setIsEditing(false);
      setFormData({
        ...formData,
        password: '',
        confirmPassword: '',
      });
    } else {
      setError(result.message);
    }
    
    setIsLoading(false);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    const result = await deleteProfile();
    
    if (!result.success) {
      setError(result.message);
      setIsDeleting(false);
    }
    // If successful, user will be logged out automatically
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
    setDeleteConfirmText('');
    setError('');
    setMessage('');
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmText === 'DELETE') {
      handleDelete();
    } else {
      setError('Please type "DELETE" to confirm account deletion');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeleteConfirmText('');
    setError('');
    setMessage('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      username: user?.username || '',
      mail: user?.mail || '',
      password: '',
      confirmPassword: '',
    });
    setError('');
    setMessage('');
    setShowDeleteConfirm(false);
    setDeleteConfirmText('');
  };

  return (
    <div className="container">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white">
              <h2 className="card-title mb-0">
                <i className="bi bi-person-circle me-2"></i>
                Profile Settings
              </h2>
            </div>
            <div className="card-body">
              {/* Profile Display */}
              {!isEditing && (
                <div>
                  <div className="row mb-4">
                    <div className="col-md-3 text-center">
                      <div 
                        className="rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center mb-3"
                        style={{ width: '100px', height: '100px', fontSize: '2.5rem', fontWeight: 'bold' }}
                      >
                        {user?.username?.charAt(0).toUpperCase()}
                      </div>
                    </div>
                    <div className="col-md-9">
                      <h3 className="mb-3">{user?.username}</h3>
                      <div className="row">
                        <div className="col-sm-3">
                          <strong>Email:</strong>
                        </div>
                        <div className="col-sm-9">
                          {user?.mail}
                        </div>
                      </div>
                      <hr />
                      <div className="row">
                        <div className="col-sm-3">
                          <strong>Member since:</strong>
                        </div>
                        <div className="col-sm-9">
                          {new Date(user?.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </div>
                      </div>
                      <hr />
                      <div className="row">
                        <div className="col-sm-3">
                          <strong>Last updated:</strong>
                        </div>
                        <div className="col-sm-9">
                          {new Date(user?.updated_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="d-flex gap-2">
                    <button 
                      className="btn btn-primary"
                      onClick={() => setIsEditing(true)}
                    >
                      <i className="bi bi-pencil me-2"></i>
                      Edit Profile
                    </button>
                    <button 
                      className="btn btn-outline-danger"
                      onClick={handleDeleteClick}
                      disabled={isDeleting}
                    >
                      <i className="bi bi-trash me-2"></i>
                      Delete Account
                    </button>
                  </div>
                </div>
              )}

              {/* Delete Confirmation Modal */}
              {showDeleteConfirm && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
                  <div className="card" style={{ maxWidth: '500px', width: '90%' }}>
                    <div className="card-header bg-danger text-white">
                      <h5 className="card-title mb-0">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        Delete Account
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="alert alert-danger" role="alert">
                        <strong>Warning:</strong> This action cannot be undone. Your account and all associated data will be permanently deleted.
                      </div>
                      
                      <p className="mb-3">
                        To confirm account deletion, please type <strong>DELETE</strong> in the box below:
                      </p>
                      
                      <div className="mb-3">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Type DELETE to confirm"
                          value={deleteConfirmText}
                          onChange={(e) => setDeleteConfirmText(e.target.value)}
                          disabled={isDeleting}
                        />
                      </div>

                      {error && (
                        <div className="alert alert-danger" role="alert">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          {error}
                        </div>
                      )}

                      <div className="d-flex gap-2 justify-content-end">
                        <button 
                          className="btn btn-secondary"
                          onClick={handleDeleteCancel}
                          disabled={isDeleting}
                        >
                          Cancel
                        </button>
                        <button 
                          className="btn btn-danger"
                          onClick={handleDeleteConfirm}
                          disabled={isDeleting || deleteConfirmText !== 'DELETE'}
                        >
                          {isDeleting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Deleting...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-trash me-2"></i>
                              Delete Account
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Profile Edit Form */}
              {isEditing && (
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="username" className="form-label fw-semibold">Username</label>
                      <input
                        type="text"
                        className="form-control"
                        id="username"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        required
                        disabled={isLoading}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="mail" className="form-label fw-semibold">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        id="mail"
                        name="mail"
                        value={formData.mail}
                        onChange={handleChange}
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="password" className="form-label fw-semibold">
                        New Password <small className="text-muted">(leave blank to keep current)</small>
                      </label>
                      <input
                        type="password"
                        className="form-control"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        disabled={isLoading}
                        minLength={6}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="confirmPassword" className="form-label fw-semibold">Confirm New Password</label>
                      <input
                        type="password"
                        className="form-control"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        disabled={isLoading || !formData.password}
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="alert alert-danger" role="alert">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {error}
                    </div>
                  )}

                  {message && (
                    <div className="alert alert-success" role="alert">
                      <i className="bi bi-check-circle me-2"></i>
                      {message}
                    </div>
                  )}

                  <div className="d-flex gap-2">
                    <button 
                      type="submit" 
                      className="btn btn-success"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Updating...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check me-2"></i>
                          Save Changes
                        </>
                      )}
                    </button>
                    <button 
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCancel}
                      disabled={isLoading}
                    >
                      <i className="bi bi-x me-2"></i>
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
